'use client';

/**
 * Prompt Library Panel (for Settings)
 *
 * Reuses the PromptLibraryModal content but renders it inline in settings
 * instead of as a modal overlay. Updated with liquid glass design.
 */

import * as React from 'react';
import { Sparkles, Library, Star } from 'lucide-react';
import { usePromptLibraryStore } from '../stores/prompt-library-store';
import { Button } from './button';
import { InfoSection } from './info-section';

export const PromptLibraryPanel: React.FC = () => {
  // ✅ CORRECT - Use selectors, not getState() during render
  const openLibraryModal = usePromptLibraryStore(state => state.openLibraryModal);
  const userPromptsCount = usePromptLibraryStore(state => state.userPrompts.length);
  const favoritesCount = usePromptLibraryStore(state => state.favoriteIds.size);
  const isPromptsLoading = usePromptLibraryStore(state => state.isPromptsLoading);
  const isPreferencesLoading = usePromptLibraryStore(state => state.isPreferencesLoading);

  const isLoading = isPromptsLoading || isPreferencesLoading;

  return (
    <div className="flex flex-col items-start h-full w-full p-4 sm:p-6 md:p-8 overflow-y-auto">
      <div className="w-full max-w-3xl mx-auto space-y-6">
        {/* Header Section */}
        <InfoSection>
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2 break-words">Prompt Library</h2>
            <p className="text-secondary-enhanced break-words">
              Manage your custom prompts and access organization-wide prompt templates.
            </p>
          </div>
        </InfoSection>

        {/* Action Button */}
        <div className="pt-4 flex justify-center">
          <Button variant="primary" size="lg" onClick={openLibraryModal} className="w-full">
            <Sparkles className="h-5 w-5" />
            Open Prompt Library
          </Button>
        </div>

        {/* Quick Stats */}
        <InfoSection variant="nested">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="flex flex-col items-center text-center p-3 sm:p-4 glass-surface-bg glass-surface-border rounded-lg">
              <div className="text-2xl font-bold text-foreground mb-1">20</div>
              <div className="text-xs text-muted-foreground">System Templates</div>
            </div>
            <div className="flex flex-col items-center text-center p-3 sm:p-4 glass-surface-bg glass-surface-border rounded-lg">
              {isLoading ? (
                <div className="h-8 w-12 bg-muted/20 rounded animate-pulse mb-1" />
              ) : (
                <div className="text-2xl font-bold text-foreground mb-1">{userPromptsCount}</div>
              )}
              <div className="text-xs text-muted-foreground">Your Prompts</div>
            </div>
            <div className="flex flex-col items-center text-center p-3 sm:p-4 glass-surface-bg glass-surface-border rounded-lg">
              {isLoading ? (
                <div className="h-8 w-12 bg-muted/20 rounded animate-pulse mb-1" />
              ) : (
                <div className="text-2xl font-bold text-foreground mb-1">{favoritesCount}</div>
              )}
              <div className="text-xs text-muted-foreground">Favorites</div>
            </div>
          </div>
        </InfoSection>

        {/* Features List */}
        <InfoSection title="Features" titleIcon={<Library className="h-5 w-5" />}>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5 flex-shrink-0">✓</span>
              <span>
                <strong className="text-foreground">20 Medical Templates:</strong> Pre-built prompts
                for prior authorization, coding, error detection, and more
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5 flex-shrink-0">✓</span>
              <span>
                <strong className="text-foreground">Custom Prompts:</strong> Create your own
                reusable prompts with variable placeholders
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5 flex-shrink-0">✓</span>
              <span>
                <strong className="text-foreground">AI Suggestions:</strong> Get context-aware
                prompt suggestions based on your screen
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5 flex-shrink-0">✓</span>
              <span>
                <strong className="text-foreground">Organization Library:</strong> Share prompts
                across your entire team
              </span>
            </li>
          </ul>
        </InfoSection>

        {/* Quick Tips */}
        <InfoSection
          variant="primary"
          title="💡 Quick Tips"
          titleIcon={<Star className="h-5 w-5" />}
        >
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0">•</span>
              <span>Access prompts from chat using the ✨ Prompts button</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0">•</span>
              <span>Use 💡 Suggest for AI-generated contextual prompts</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0">•</span>
              <span>Add variables with {'{{variable_name}}'} syntax</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="flex-shrink-0">•</span>
              <span>Star your favorites for quick access</span>
            </li>
          </ul>
        </InfoSection>
      </div>
    </div>
  );
};
